import type { Candle, Marker, Overlay } from "@/components/chart";
import { AVERAGES } from "@/components/chart/averages";

import type { Bar, ChartMarker } from "./types";

/** "1D" is the intraday chart (today, or the last session); the rest are daily bars. */
export const PERIODS = ["1D", "3M", "6M", "1Y", "3Y", "5Y"] as const;
export type Period = (typeof PERIODS)[number];

export const OVERLAYS = [
  ...AVERAGES.map((a) => ({ id: a.id, label: a.label })),
  { id: "st", label: "Supertrend" },
] as const;
export type OverlayId = (typeof OVERLAYS)[number]["id"];
export const DEFAULT_OVERLAYS: OverlayId[] = ["sma50", "sma200"];

export function parseOverlays(value: unknown): OverlayId[] {
  if (typeof value !== "string") return DEFAULT_OVERLAYS;
  if (value === "none") return [];
  const ids = new Set(OVERLAYS.map((o) => o.id as string));
  return value.split(",").filter((v): v is OverlayId => ids.has(v));
}

export function formatOverlays(ids: OverlayId[]): string {
  return ids.length ? ids.join(",") : "none";
}

/** A link to a stock page. Routes are built at runtime, so links are plain paths. */
export function stockHref(symbol: string, search?: Record<string, string>): string {
  const qs = search ? `?${new URLSearchParams(search).toString()}` : "";
  return `/app/stocks/${encodeURIComponent(symbol)}${qs}`;
}

export function toCandles(bars: Bar[]): Candle[] {
  return bars.map((b) => ({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume }));
}

/**
 * Overlay lines. Supertrend is split into one line per run of the same
 * direction (green under price in an uptrend, red above it in a downtrend):
 * a single line would join the end of one up-run to the start of the next.
 */
export function toOverlays(bars: Bar[], shown: OverlayId[]): Overlay[] {
  const out: Overlay[] = [];
  // the averages come with the bars (worked out on the server with earlier history)
  for (const a of AVERAGES) {
    if (!shown.includes(a.id)) continue;
    out.push({ id: a.id, title: a.label, color: a.color, lineWidth: 2, data: bars.map((b) => ({ time: b.time, value: b[a.id] ?? null })) });
  }
  if (shown.includes("st")) {
    type Run = { dir: number; data: { time: string; value: number }[] };
    const runs: Run[] = [];
    let run: Run | null = null;
    for (const b of bars) {
      if (b.supertrend == null || b.supertrend_dir == null) {
        run = null;
        continue;
      }
      if (run === null || run.dir !== b.supertrend_dir) {
        run = { dir: b.supertrend_dir, data: [] };
        runs.push(run);
      }
      run.data.push({ time: b.time, value: b.supertrend });
    }
    runs.forEach((r, i) =>
      out.push({ id: `st-${i}`, color: r.dir > 0 ? "up" : "down", lineWidth: 1, dashed: true, data: r.data }),
    );
  }
  return out;
}

export function toMarkers(markers: ChartMarker[]): Marker[] {
  return markers.map((m) =>
    m.kind === "results"
      ? { time: m.time, position: "belowBar", shape: "circle", color: "accent", text: "R" }
      : { time: m.time, position: "aboveBar", shape: "arrowDown", color: "brand", text: m.label },
  );
}

export const MARKER_NAMES: Record<ChartMarker["kind"], string> = {
  results: "Results",
  bonus: "Bonus",
  split: "Split",
  adjustment: "Price adjustment",
};
